import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { cmsApi } from '@/services/api';
import { useNavCategories } from '@/context/NavCategoriesContext';
import { Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';

export const Footer = () => {
  const { categories: navCategories } = useNavCategories();
  const [siteContent, setSiteContent] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await cmsApi.getContent();
        setSiteContent(res.data);
      } catch (error) {
        console.error('Could not fetch site content:', error);
      }
    };
    fetchContent();
  }, []);

  const footer = siteContent?.footer || {};

  return (
    <footer className="bg-[#0B1E33] text-white" data-testid="footer">
      <div className="container-main py-12 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {/* Column 1: About */}
          <div>
            <div className="flex items-center mb-6">
              <span className="text-xl font-heading font-black text-white">Bygg</span>
              <span className="text-xl font-heading font-black text-[#F97316]">Fynd</span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              {footer.about_text || 'Sveriges marknadsplats för byggvaror. Hitta rätt material till rätt pris.'}
            </p>
            <div className="flex gap-4 mt-6">
              {footer.facebook_url && (
                <a href={footer.facebook_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#F97316] transition-colors" aria-label="Facebook">
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {footer.instagram_url && (
                <a href={footer.instagram_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#F97316] transition-colors" aria-label="Instagram">
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {footer.twitter_url && (
                <a href={footer.twitter_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#F97316] transition-colors" aria-label="Twitter">
                  <Twitter className="h-5 w-5" />
                </a>
              )}
              {footer.linkedin_url && (
                <a href={footer.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#F97316] transition-colors" aria-label="LinkedIn">
                  <Linkedin className="h-5 w-5" />
                </a>
              )}
              {/* Show default icons if no URLs set */}
              {!footer.facebook_url && !footer.instagram_url && !footer.twitter_url && !footer.linkedin_url && (
                <>
                  <span className="text-gray-500"><Facebook className="h-5 w-5" /></span>
                  <span className="text-gray-500"><Instagram className="h-5 w-5" /></span>
                  <span className="text-gray-500"><Twitter className="h-5 w-5" /></span>
                  <span className="text-gray-500"><Linkedin className="h-5 w-5" /></span>
                </>
              )}
            </div>
          </div>

          {/* Column 2: Categories */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wide mb-4">Kategorier</h4>
            <ul className="space-y-2">
              {navCategories.map((cat) => (
                <li key={cat.id}>
                  <Link
                    to={`/produkter?category=${cat.id}`}
                    className="text-gray-400 hover:text-white text-sm transition-colors"
                    data-testid={`footer-category-${cat.id}`}
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Information */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wide mb-4">Information</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/om-oss" className="text-gray-400 hover:text-white text-sm transition-colors">
                  Om oss
                </Link>
              </li>
              <li>
                <Link to="/kopvillkor" className="text-gray-400 hover:text-white text-sm transition-colors">
                  Köpvillkor & Ångerrätt
                </Link>
              </li>
              <li>
                <Link to="/integritetspolicy" className="text-gray-400 hover:text-white text-sm transition-colors">
                  Integritetspolicy
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-gray-400 hover:text-white text-sm transition-colors">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/bli-partner" className="text-gray-400 hover:text-white text-sm transition-colors">
                  Bli partner
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Contact */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wide mb-4">Kontakt</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>
                <Link to="/kontakt" className="hover:text-white transition-colors">
                  Kontakta oss
                </Link>
              </li>
              <li>{footer.contact_email || 'info@byggfynd.se'}</li>
              <li>{footer.contact_phone || '08-123 456 78'}</li>
              {footer.contact_address && (
                <li className="pt-2 whitespace-pre-line">
                  {footer.contact_address}
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-700/50 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} ByggFynd AB. Alla rättigheter förbehållna.
            </p>
            <div className="flex gap-6 text-sm text-gray-500">
              <Link to="/kopvillkor" className="hover:text-[#F97316] transition-colors">
                Villkor
              </Link>
              <Link to="/integritetspolicy" className="hover:text-[#F97316] transition-colors">
                Integritet
              </Link>
              <Link to="/cookies" className="hover:text-[#F97316] transition-colors">
                Cookies
              </Link>
            </div>
          </div>
          {/* Made with Emergent - Discrete */}
          <div className="mt-8 text-center">
            <span className="made-with-emergent">
              Byggd med ❤️ av ByggFynd Team
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};
